/**
 * 场景添加对话框（与 CharacterCreateDialog / PropCreateDialog 同构）
 *
 * 两种模式：
 * - 新建：填名字 + 描述，提交时直接落入"已加入项目"
 * - 从场景库挑选：列出当前用户的所有未归档 / 未加入本项目的场景，复选后批量加入
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, X, MapPin, Plus } from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError, type DirectorScene } from '../../services/directorApi';
import { AssetPickerListItem } from '../shared/AssetPickerListItem';
import { AssetPreviewPopover } from '../shared/AssetPreviewPopover';

interface Props {
  open: boolean;
  projectId: string;
  /** 当前项目里已经加入的场景 ID 列表，picker 中会过滤掉避免重复 */
  existingSceneIds?: string[];
  onClose: () => void;
  onCreated: () => void;
}

type Mode = 'new' | 'pick';

export function SceneCreateDialog({
  open,
  projectId,
  existingSceneIds = [],
  onClose,
  onCreated,
}: Props) {
  const [mode, setMode] = useState<Mode>('new');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [scenes, setScenes] = useState<DirectorScene[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /** 当前预览的场景 ID */
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewing = useMemo(
    () => (previewId ? scenes.find((s) => s.id === previewId) ?? null : null),
    [previewId, scenes]
  );

  useEffect(() => {
    if (!open || mode !== 'pick') return;
    setLoadingList(true);
    directorApi
      .listMyScenes(false)
      .then((list) => {
        const existingSet = new Set(existingSceneIds);
        setScenes(list.filter((s) => !existingSet.has(s.id)));
      })
      .catch((err) => {
        toast.error(err instanceof DirectorApiError ? err.message : '加载场景库失败');
      })
      .finally(() => setLoadingList(false));
  }, [open, mode, existingSceneIds]);

  if (!open) return null;

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmitNew = async () => {
    if (!name.trim()) {
      toast.error('场景名不能为空');
      return;
    }
    setSubmitting(true);
    try {
      await directorApi.createScene(projectId, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success('场景已创建');
      setName('');
      setDescription('');
      onCreated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPick = async () => {
    if (selectedIds.size === 0) {
      toast.error('请至少选择一个场景');
      return;
    }
    setSubmitting(true);
    try {
      for (const id of selectedIds) {
        await directorApi.addSceneToProject(projectId, id);
      }
      toast.success(`已加入 ${selectedIds.size} 个场景`);
      setSelectedIds(new Set());
      onCreated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '加入失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-emerald-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <MapPin className="h-3 w-3 text-emerald-200" />
                添加场景
              </div>
              <h2 className="text-lg font-semibold leading-tight">为分镜准备视觉风格</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 模式切换 */}
        <div className="grid grid-cols-2 gap-1.5 bg-slate-50 px-5 pt-4">
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`inline-flex items-center justify-center gap-1.5 rounded-2xl px-3 py-2 text-sm font-semibold transition ${
              mode === 'new'
                ? 'bg-slate-950 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Plus className="h-4 w-4" />
            新建场景
          </button>
          <button
            type="button"
            onClick={() => setMode('pick')}
            className={`inline-flex items-center justify-center gap-1.5 rounded-2xl px-3 py-2 text-sm font-semibold transition ${
              mode === 'pick'
                ? 'bg-slate-950 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            <MapPin className="h-4 w-4" />
            从场景库挑选
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50">
          {mode === 'new' ? (
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  名字 <span className="text-rose-500">*</span>
                </label>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 60))}
                  placeholder="例如：古风书院"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  描述（可选）
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                  placeholder="例如：青砖灰瓦，竹林环绕，木门半掩"
                  rows={3}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400">{description.length}/500</p>
              </div>
              <p className="text-xs leading-5 text-slate-500">
                创建后可在场景详情中生成设定图，或从作品库导入。场景为用户级共享资产，可跨项目复用。
              </p>
            </div>
          ) : (
            <div className="p-5">
              {loadingList ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                </div>
              ) : scenes.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  没有可挑选的场景
                  <p className="mt-1 text-xs">先用「新建场景」创建一个</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {scenes.map((s) => (
                    <AssetPickerListItem
                      key={s.id}
                      kind="scene"
                      selected={selectedIds.has(s.id)}
                      name={s.name}
                      description={s.description}
                      thumbnailUrl={s.settingThumbnailUrl ?? s.settingUrl ?? null}
                      onToggleSelect={() => toggleSelected(s.id)}
                      onPreview={() => setPreviewId(s.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {previewing && (
          <AssetPreviewPopover
            open={!!previewing}
            onClose={() => setPreviewId(null)}
            kind="scene"
            imageUrl={previewing.settingUrl ?? previewing.settingThumbnailUrl ?? null}
            name={previewing.name}
            description={previewing.description}
            createdAt={previewing.createdAt}
            updatedAt={previewing.updatedAt}
          />
        )}

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          {mode === 'new' ? (
            <button
              type="button"
              onClick={handleSubmitNew}
              disabled={submitting || !name.trim()}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              创建
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmitPick}
              disabled={submitting || selectedIds.size === 0}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              加入项目（{selectedIds.size}）
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
